import React from "react";
import { Formik, Form, Field as FormikField } from "formik";
import * as Yup from "yup";
import { MapPin, Storefront, WarningCircle } from "@phosphor-icons/react";

import { MarketShell } from "../../design-system/components/shells";
import { Empty, LoadingBlock, PageTitle } from "../../design-system/components/desktop";
import {
  AuthBanner,
  AuthConsent,
  AuthField,
  AuthFields,
  AuthInput,
  AuthSubmit,
} from "../../design-system/patterns/auth";
import {
  Panel,
  PanelAction,
  PanelRow,
  PanelRows,
} from "../../design-system/patterns/settings";
import { routes } from "../../routes/routes";

import AddressModal from "./AddressModal";
import "./SellerRegistration.css";

/**
 * /seller-registration, the view. Pure.
 *
 * WHY THE AUTH PATTERN AND NOT A NEW ONE
 * --------------------------------------
 * This is the same shape as signup: a stack of labelled fields, a consent row
 * carrying two links, and one block submit. `patterns/auth` already owns every
 * one of those, including `AuthConsent`, which was promoted precisely because
 * signup and this form both have an "I agree to X and Y" row. So it is reused
 * here rather than rebuilt, and `SellerRegistration.css`'s 410 lines of
 * `.field-input`, `.checkbox-container` and `.submit-button` are gone.
 *
 * It does NOT take `AuthShell`, though. Auth is a no-shell centred card because
 * the user is not signed in yet; this user IS signed in and is applying for a
 * role, so it mounts in `MarketShell` with the marketplace still behind it.
 * `CONVERSION-MAP` B10 says MarketShell for exactly that reason.
 *
 * WHAT CHANGED IN THE BEHAVIOUR, AND WHY
 * --------------------------------------
 * The two `⚠️` warning paragraphs under the submit are gone. They restated, in
 * emoji, two conditions the disabled button already encodes, and they appeared
 * BEFORE the user had touched anything, so a fresh form scolded you for not
 * having agreed to terms you had not been shown yet. The address requirement is
 * now stated by the address panel itself, where the fix is; the terms
 * requirement is a validation error on the consent row, where the checkbox is.
 *
 * The submit's `title` attribute went with them. A tooltip on a DISABLED button
 * does not fire in most browsers, so it was explaining nothing to anybody.
 */

export const sellerRegistrationValidationSchema = Yup.object({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  phone_number: Yup.string()
    .matches(
      /^(\+27|0)[0-9]{9}$/,
      "Phone number must be a valid South African number (e.g., 0821234567 or +27821234567)",
    )
    .required("Phone number is required"),
  store_name: Yup.string()
    .min(5, "Store name must be at least 5 characters")
    .max(20, "Store name must be at most 20 characters")
    .matches(/^[a-zA-Z\s]+$/, "Store name can only contain letters and one space")
    .required("Store name is required"),
  agreeToTerms: Yup.boolean()
    .oneOf([true], "You must agree to the Terms of Service and Privacy Policy")
    .required("You must agree to the Terms of Service and Privacy Policy"),
});

const formatAddress = (addr) =>
  [addr?.address_line1, addr?.address_line2, addr?.city, addr?.province, addr?.postal_code]
    .filter(Boolean)
    .join(", ");

export function SellerRegistrationView({
  onSubmit,
  userProfile,
  defaultAddress,
  loadingAddress = false,
  error,

  addressModalOpen = false,
  addressModalLoading = false,
  onOpenAddressModal,
  onCloseAddressModal,
  onSubmitAddress,

  initialValues,
  initialErrors,
  initialTouched,
  loading = false,
}) {
  const values = initialValues || {
    first_name: userProfile?.first_name || "",
    last_name: userProfile?.last_name || "",
    phone_number: "",
    store_name: "",
    agreeToTerms: false,
  };

  return (
    <MarketShell
      title="Become a seller"
      breadcrumbs={[
        { label: "Marketplace", to: routes.MARKETPLACE },
        { label: "Become a seller" },
      ]}
    >
      <PageTitle>Become a Sleevr seller</PageTitle>
      <p className="srx-lead">
        List your cards to collectors across South Africa. We need a few details
        before your store can go live.
      </p>

      <AuthBanner tone="error">{error}</AuthBanner>

      <Formik
        enableReinitialize
        initialValues={values}
        initialErrors={initialErrors}
        initialTouched={initialTouched}
        validationSchema={sellerRegistrationValidationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors, touched }) => {
          const busy = isSubmitting || loading;
          const shown = (name) => (touched[name] && errors[name] ? errors[name] : undefined);

          return (
            <Form className="srx-form" noValidate>
              <Panel icon={Storefront} title="Who you are">
                <AuthFields>
                  <AuthField label="First name" error={shown("first_name")}>
                    <FormikField
                      as={AuthInput}
                      name="first_name"
                      autoComplete="given-name"
                      disabled={busy}
                    />
                  </AuthField>

                  <AuthField label="Last name" error={shown("last_name")}>
                    <FormikField
                      as={AuthInput}
                      name="last_name"
                      autoComplete="family-name"
                      disabled={busy}
                    />
                  </AuthField>

                  <AuthField label="Phone number" error={shown("phone_number")}>
                    <FormikField
                      as={AuthInput}
                      name="phone_number"
                      type="tel"
                      placeholder="0821234567"
                      autoComplete="tel"
                      disabled={busy}
                    />
                  </AuthField>
                </AuthFields>
              </Panel>

              <Panel
                icon={MapPin}
                title="Default address"
                sub="Where couriers collect your orders from."
                action={
                  defaultAddress ? (
                    <PanelAction onClick={onOpenAddressModal}>Change</PanelAction>
                  ) : null
                }
              >
                {loadingAddress ? (
                  <LoadingBlock>Loading your address</LoadingBlock>
                ) : defaultAddress ? (
                  <PanelRows>
                    <PanelRow
                      title={defaultAddress.title || defaultAddress.city || "Default address"}
                      detail={formatAddress(defaultAddress)}
                    />
                  </PanelRows>
                ) : (
                  <Empty
                    icon={MapPin}
                    title="No default address yet"
                    text="Add one to continue. Your application cannot be submitted without it."
                    inline
                    actions={
                      <PanelAction onClick={onOpenAddressModal}>Add default address</PanelAction>
                    }
                  />
                )}
              </Panel>

              <Panel icon={Storefront} title="Your store">
                <AuthFields>
                  <AuthField
                    label="Store name"
                    hint="5 to 20 characters, letters and single spaces only."
                    error={shown("store_name")}
                  >
                    <FormikField
                      as={AuthInput}
                      name="store_name"
                      placeholder="Enter your store name"
                      maxLength={20}
                      disabled={busy}
                    />
                  </AuthField>
                </AuthFields>
              </Panel>

              <FormikField name="agreeToTerms">
                {({ field }) => (
                  <AuthConsent
                    name="agreeToTerms"
                    checked={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={busy}
                    error={shown("agreeToTerms")}
                  >
                    {/* The consent WORDING is legal copy and is unchanged. The
                        two links are not: the app has no /terms and no /privacy
                        route, so the old markup pointed both at `href="#"`,
                        which reloads the page and loses the form. A link to
                        nowhere is worse than no link, so the phrases are plain
                        text until the routes exist. FLAGGED: a consent checkbox
                        whose terms cannot be read is a product problem, not a
                        design one. */}
                    I agree to the Terms of Service and the Privacy Policy
                  </AuthConsent>
                )}
              </FormikField>

              <AuthSubmit
                loading={busy}
                loadingLabel="Completing registration"
                disabled={!defaultAddress}
              >
                Complete registration
              </AuthSubmit>

              {!defaultAddress && !loadingAddress ? (
                <p className="srx-blocked" role="status">
                  <WarningCircle aria-hidden="true" />
                  <span>Add a default address above before you can submit.</span>
                </p>
              ) : null}
            </Form>
          );
        }}
      </Formik>

      <AddressModal
        isOpen={addressModalOpen}
        onClose={onCloseAddressModal}
        onSubmit={onSubmitAddress}
        loading={addressModalLoading}
      />
    </MarketShell>
  );
}

export default SellerRegistrationView;
